// ABOUTME: Orchestration hook for the Operational Hours entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useOperationalHoursConfig } from './useOperationalHoursConfig'
import { useCreateOperationalHours, useCreateOperationalHoursWithScope, useUpdateOperationalHours, useDeleteOperationalHours } from './useOperationalHoursMutations'
import { OperationalHoursConfig, OperationalHoursFormValues, OperationalHoursOverrides } from '../types/operationalHours'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseOperationalHoursEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: OperationalHoursFormValues) => void
}

interface UseOperationalHoursEntityResult {
  config: OperationalHoursConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

const equalsParent = (values: OperationalHoursFormValues, config: OperationalHoursConfig): boolean =>
  JSON.stringify(values) === JSON.stringify(config.parentValues)

export const useOperationalHoursEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseOperationalHoursEntityParams): UseOperationalHoursEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useOperationalHoursConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    timeZone: cfg.timeZone,
    businessDays: cfg.businessDays,
    standardDays: cfg.standardDays,
    standardHours: cfg.standardHours,
    nonStandardHours: cfg.nonStandardHours,
    nonWorkingDays: cfg.nonWorkingDays,
  })), [rawConfig, pendingScope])

  const createMutation = useCreateOperationalHours(countryId, businessLine)
  const createWithScopeMutation = useCreateOperationalHoursWithScope(countryId, businessLine)
  const updateMutation = useUpdateOperationalHours(countryId, businessLine)
  const deleteMutation = useDeleteOperationalHours(countryId, businessLine)

  const formValuesRef = useRef<OperationalHoursFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<OperationalHoursConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: OperationalHoursFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        const overrides: OperationalHoursOverrides = { timeZone: true, businessDays: true, operatingSchedule: true, nonWorkingDays: true }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values, overrides }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      // Override-all: saving at any scope marks all sections as overridden (no partial section overrides yet)
      const overrides: OperationalHoursOverrides = {
        timeZone: true, businessDays: true, operatingSchedule: true, nonWorkingDays: true
      }
      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values, overrides }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
